import React from 'react';
import styled, { css } from 'styled-components';
import type { GroupBase, SingleValue, StylesConfig } from 'react-select';
import Select from 'react-select';

import { useField, useFormikContext } from 'formik';
import { useTranslation } from '@lt/localization';
import type { Citizenship } from '../../../../types';
import { RUSSIA_NATIONALITY_ID } from '../../constants';

type CustomerCitizenshipProps = {
  className?: string;
  citizenships: Citizenship[];
  isMobile: boolean;
};

const getSelectStyles: (
  isMobile: boolean,
) => StylesConfig<Citizenship, false, GroupBase<Citizenship>> = (isMobile) => ({
  control: (provided) => ({
    ...provided,
    borderRadius: isMobile ? '4px 0 0 4px' : 0,
    height: '48px',
    boxShadow: 'none',
    border: `1px solid #c2c5cc`,
    background: '#f1fbe8',
    color: '#17181a',
    fontWeight: isMobile ? 'bold' : 700,
    ...(isMobile ? { fontSize: '18px' } : {}),
  }),
});

const customComponents = {
  IndicatorSeparator: () => null,
};

const CustomerCitizenship = (props: CustomerCitizenshipProps) => {
  const { citizenships, className, isMobile } = props;

  const [field] = useField('nationalityId');

  const { setFieldValue } = useFormikContext();

  const handleChange = (data: SingleValue<Citizenship>) => {
    setFieldValue('nationalityId', data?.value);

    const isRussian = data?.value === RUSSIA_NATIONALITY_ID;
    setFieldValue(
      'docType',
      isRussian ? 'internal_passport' : 'international_passport',
    );
  };

  const { t } = useTranslation();

  return (
    <SelectWrapper className={className}>
      <SelectHeader $isMobile={isMobile}>
        {t('checkout.customer.form_fields.citizenship')}
      </SelectHeader>
      <Select<Citizenship>
        components={customComponents}
        options={citizenships}
        isSearchable={false}
        value={citizenships.find(
          (citizenship) => citizenship.value === field.value,
        )}
        defaultValue={citizenships[0]}
        styles={getSelectStyles(isMobile)}
        onChange={handleChange}
      />
    </SelectWrapper>
  );
};

const SelectWrapper = styled.div`
  min-width: 110px;
`;

const SelectHeader = styled.div<{ $isMobile: boolean }>`
  color: #4d4d4d;

  font-size: 12px;
  line-height: 16px;
  margin-bottom: 4px;

  ${(props) =>
    props.$isMobile &&
    css`
      font-size: 9px;
      line-height: 12px;
      letter-spacing: 0.9px;
      text-transform: uppercase;
    `}
`;

export default CustomerCitizenship;
